import { CACHED_WIKI_IMAGES, WikiCacheVariant } from "@shared/wiki-image-cache";

function extractFilename(url: string): string | null {
  const m = url.match(/\/thumb\/[a-f0-9]\/[a-f0-9]{2}\/([^/]+?)\/\d+px-\1/);
  return m ? m[1] : null;
}

/** Pick the smallest cached variant >= width, else the largest available. */
function pickVariant(variants: ReadonlyArray<WikiCacheVariant>, width: number): WikiCacheVariant {
  for (const v of variants) if (v[0] >= width) return v;
  return variants[variants.length - 1];
}

/** Returns local cached URL when a variant exists for this filename, else null. */
function tryLocal(url: string, width: number): string | null {
  const filename = extractFilename(url);
  if (!filename) return null;
  const variants = CACHED_WIKI_IMAGES.get(filename);
  if (!variants || variants.length === 0) return null;
  const v = pickVariant(variants, width);
  // encodeURIComponent so that literal % signs in the filename become %25,
  // allowing the static file server to decode them back to % and find the file.
  return `/img-cache/${encodeURIComponent(filename)}/${v[0]}.${v[1]}`;
}

/** True only for Wikimedia-hosted URLs that the resizer logic actually understands. */
function isWikimediaUrl(url: string): boolean {
  return url.includes("wikimedia.org") || url.includes("wikipedia.org");
}

/**
 * Build the target Wikimedia thumbnail URL at the requested width.
 * This is the raw Wikimedia URL — it should only be used server-side or
 * routed through the proxy; never used as a direct <img> src.
 */
function wikimediaThumbUrl(url: string, width: number): string {
  if (url.includes("Special:FilePath")) {
    return /\?width=\d+/.test(url)
      ? url.replace(/\?width=\d+/, `?width=${width}`)
      : url + (url.includes("?") ? "&" : "?") + `width=${width}`;
  }
  // Standard thumb URL: replace existing size or append width to direct URLs
  if (url.includes("/thumb/")) {
    return url.replace(/\/\d+px-/, `/${width}px-`);
  }
  // Direct (non-thumb) Wikimedia URL — proxy it as-is (server will serve original size)
  return url;
}

/**
 * Route a Wikimedia image through our server proxy so the correct
 * User-Agent header is sent. Wikimedia requires a descriptive User-Agent
 * to serve thumbnail sizes that haven't been pre-generated; browsers
 * cannot set this header, causing 400 responses.
 */
function proxyUrl(wikimediaUrl: string): string {
  return `/api/img-proxy?url=${encodeURIComponent(wikimediaUrl)}`;
}

/** Get a Wikimedia thumb URL at the requested width — returns local cache when present,
 *  otherwise routes through the server proxy so the User-Agent header is set correctly.
 *  Non-Wikimedia URLs (local /public assets, third-party CDNs, etc.) are returned unchanged. */
export function wikiSize(url: string, width: number): string {
  const local = tryLocal(url, width);
  if (local) return local;
  if (!isWikimediaUrl(url)) return url;
  return proxyUrl(wikimediaThumbUrl(url, width));
}

/** Get a Wikimedia WebP thumb URL at the requested width — returns local cache when present,
 *  otherwise routes through the server proxy.
 *  Non-Wikimedia URLs are returned unchanged (no `.webp` suffix appended). */
export function wikiWebP(url: string, width: number): string {
  const local = tryLocal(url, width);
  if (local) return local;
  if (!isWikimediaUrl(url)) return url;
  // Special:FilePath URLs don't support .webp suffix
  if (url.includes("Special:FilePath")) return proxyUrl(wikimediaThumbUrl(url, width));
  return proxyUrl(wikimediaThumbUrl(url, width) + ".webp");
}

/** Hero-sized URL — uses local cache when available, else proxied Wikimedia thumb.
 *  For thumb URLs, caps the requested size at the size already present in the URL
 *  so we never request a size Wikimedia hasn't pre-generated (which returns 400).
 *  Non-Wikimedia URLs are returned unchanged. */
export function heroUrl(url: string): string {
  const local = tryLocal(url, 1920);
  if (local) return local;
  if (!isWikimediaUrl(url)) return url;
  if (url.includes("Special:FilePath")) {
    const sized = /\?width=\d+/.test(url)
      ? url.replace(/\?width=\d+/, "?width=1920")
      : url + (url.includes("?") ? "&" : "?") + "width=1920";
    return proxyUrl(sized);
  }
  if (url.includes("/thumb/")) {
    // Never upscale beyond the pre-generated size already in the URL
    const existing = url.match(/\/(\d+)px-/);
    const targetSize = existing ? Math.min(1920, parseInt(existing[1])) : 1920;
    const sized = url.replace(/\/\d+px-/g, `/${targetSize}px-`);
    return proxyUrl(sized);
  }
  // Direct (non-thumb) Wikimedia URL — proxy as-is at original resolution
  return proxyUrl(url);
}
